import React, { useMemo } from 'react';
import { cn } from '../../utils/cn';
import { Session, Epic } from '../../types';

interface ChartPoint {
    value: number;
    date?: string;
    x?: number;
    y?: number;
}

interface TrendSparklineProps {
    data: (number | ChartPoint)[];
    width?: number;
    height?: number;
    strokeWidth?: number;
    color?: string;
    showDots?: boolean;
    className?: string;
}

/**
 * TrendSparkline - A simple SVG sparkline component
 */
export function TrendSparkline({
    data = [],
    width = 60,
    height = 20,
    strokeWidth = 1.5,
    color = 'currentColor',
    showDots = false,
    className
}: TrendSparklineProps) {
    const pathData = useMemo(() => {
        if (!data || data.length < 2) return null;

        const values = data.map(d => typeof d === 'number' ? d : d.value);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min || 1;

        const points = values.map((val, idx) => {
            const x = (idx / (values.length - 1)) * width;
            const y = height - ((val - min) / range) * (height - 4) - 2;
            return { x, y };
        });

        const pathD = points.map((p, i) =>
            `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
        ).join(' ');

        return { pathD, points, min, max };
    }, [data, width, height]);

    if (!pathData) {
        return (
            <div
                className={cn("flex items-center justify-center text-[8px] text-siemens-text-soft", className)}
                style={{ width, height }}
            >
                No data
            </div>
        );
    }

    // Determine trend color
    const trendColor = useMemo(() => {
        if (data.length < 2) return color;
        const firstItem = data[0];
        const lastItem = data[data.length - 1];
        const first = typeof firstItem === 'number' ? firstItem : firstItem.value;
        const last = typeof lastItem === 'number' ? lastItem : lastItem.value;
        if (last > first) return '#10B981'; // green
        if (last < first) return '#EF4444'; // red
        return '#6B7280'; // gray
    }, [data, color]);

    return (
        <svg
            width={width}
            height={height}
            className={cn("overflow-visible", className)}
            viewBox={`0 0 ${width} ${height}`}
        >
            <defs>
                <linearGradient id={`sparkline-gradient-${data.length}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={trendColor} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
                </linearGradient>
            </defs>

            <path
                d={`${pathData.pathD} L ${width} ${height} L 0 ${height} Z`}
                fill={`url(#sparkline-gradient-${data.length})`}
            />

            <path
                d={pathData.pathD}
                fill="none"
                stroke={trendColor}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {showDots && pathData.points.map((point: any, idx: number) => (
                <circle
                    key={idx}
                    cx={point.x}
                    cy={point.y}
                    r={2}
                    fill={trendColor}
                />
            ))}

            <circle
                cx={pathData.points[pathData.points.length - 1].x}
                cy={pathData.points[pathData.points.length - 1].y}
                r={2.5}
                fill={trendColor}
            />
        </svg>
    );
}

interface HealthTrendChartProps {
    sessions?: Session[];
    width?: number;
    height?: number;
}

export function HealthTrendChart({ sessions = [], width = 200, height = 60 }: HealthTrendChartProps) {
    const trendData = useMemo(() => {
        if (!sessions || sessions.length < 2) return [];

        return sessions.slice(-12).map(session => {
            if (session.metadata?.avgScore !== undefined) {
                // healthScore isn't in metadata type, maybe using avgScore? 
                // Original JS code used metadata.healthScore BUT createSession used avgScore.
                // Let's assume avgScore is good proxy or calculate ratio if needed.
                // Actually original code checked healthScore THEN calculated ratio.
                // My new metadata has avgScore.
                // Let's calculate health ratio: (total - unassigned) / total

                const total = session.epics.length;
                const unassigned = session.epics.filter(e => e.lane === 'Unassigned').length;
                const assignedRatio = total > 0 ? ((total - unassigned) / total) * 100 : 100;
                return { value: Math.round(assignedRatio), date: session.createdAt }; // Use createdAt
            }

            return { value: 0, date: session.createdAt };
        });
    }, [sessions]);

    if (trendData.length < 2) {
        return (
            <div className="flex items-center justify-center h-full text-xs text-siemens-text-soft">
                Need 2+ sessions for trend
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <TrendSparkline
                data={trendData}
                width={width}
                height={height}
                strokeWidth={2}
                showDots={trendData.length <= 6}
            />
            <div className="flex justify-between text-[9px] text-siemens-text-soft">
                <span>{new Date(trendData[0].date!).toLocaleDateString()}</span>
                <span>{new Date(trendData[trendData.length - 1].date!).toLocaleDateString()}</span>
            </div>
        </div>
    );
}

interface EpicScoreTrendProps {
    epicId?: string;
    sessions?: Session[];
    width?: number;
    height?: number;
}

export function EpicScoreTrend({ epicId, sessions = [], width = 50, height = 16 }: EpicScoreTrendProps) {
    const scoreHistory = useMemo(() => {
        if (!sessions || sessions.length < 2 || !epicId) return [];

        return sessions.slice(-8).map(session => {
            const epic = session.epics?.find(e => e.id === epicId);
            return (epic as Epic)?.wsjf || null;
        }).filter((v): v is number => v !== null);
    }, [epicId, sessions]);

    if (scoreHistory.length < 2) return null;

    return (
        <TrendSparkline
            data={scoreHistory}
            width={width}
            height={height}
            strokeWidth={1}
        />
    );
}

export default TrendSparkline;
